import { Component, type ErrorInfo, type ReactNode } from 'react'
import { isRouteErrorResponse, useRouteError } from 'react-router-dom'
import { WarningOctagon } from '@phosphor-icons/react'
import { Button, EmptyState } from '@/ui'
import { useI18n } from '@/i18n'
import { NotFoundPage } from './NotFoundPage'

function ErrorScreen({ detail }: { detail?: string }) {
  const { t } = useI18n()
  return (
    <main className="flex min-h-dvh items-center justify-center bg-paper px-4">
      <EmptyState
        icon={<WarningOctagon />}
        title={t('common.somethingWrong')}
        description={
          <>
            {t('common.somethingWrongBody')}
            {detail && (
              <span className="mt-3 block break-words font-mono text-[12px] text-muted">
                {detail}
              </span>
            )}
          </>
        }
        action={
          <Button size="touch" onClick={() => window.location.reload()}>
            {t('common.reload')}
          </Button>
        }
      />
    </main>
  )
}

/** Last-resort boundary around providers. Route errors are handled by RouteError below. */
export class AppErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state = { error: null as Error | null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Unhandled UI error', error, info.componentStack)
  }

  override render() {
    if (this.state.error)
      return <ErrorScreen detail={import.meta.env.DEV ? this.state.error.message : undefined} />
    return this.props.children
  }
}

/** errorElement for the router: 404s from loaders and render errors inside routes. */
export function RouteError() {
  const error = useRouteError()
  if (isRouteErrorResponse(error) && error.status === 404) return <NotFoundPage />
  console.error('Route error', error)
  const detail = import.meta.env.DEV && error instanceof Error ? error.message : undefined
  return <ErrorScreen detail={detail} />
}
